/**
 * Die zwei Anzeigedichten aus §7 — „einfach" und „experte".
 *
 * ## Was der Schalter ändert und was nicht
 *
 * Er ändert: gerundete Zahlen mit Einheit gegen rohe Werte, Koordinaten,
 * Tarifzeilen, Buchungsnummern, Zeilenhöhe (40 → 32 px). Er ändert **nicht**:
 * Reiter, Reihenfolge, Gliederung. Es ist dieselbe Fläche, nicht ein zweites
 * Produkt — wer umschaltet, muss sich sofort zurechtfinden.
 *
 * ## Warum ein Attribut am Wurzelelement und kein Prop durch elf Panels
 *
 * Der Hebel steckt in `token/anwendung.css`: `[data-dichte]` setzt die zwei
 * Maße und schaltet `.nur-experte` / `.nur-einfach`. Eine Klasse im Markup
 * genügt damit für die allermeisten Stellen, und die Panels bleiben von der
 * Frage unberührt. Nur wo eine **andere Zahl** entsteht (gerundet gegen roh),
 * braucht es den Wert im Code — dafür ist `istExperte` da.
 *
 * ## Warum das hier steht und nicht in `App.svelte`
 *
 * Prüfbarkeit: Die Tests laufen ohne Browser. Der Name des Attributs, die
 * Vorgabe und die Umrechnung Konto → Attribut sind gewöhnliche Funktionen und
 * damit prüfbar; ein `document.documentElement.setAttribute` im Bauteil wäre
 * es nicht.
 *
 * **Anzeigedichte, kein Schutz.** Nichts, was `.nur-experte` verbirgt, darf
 * schützenswert sein — es steht im ausgelieferten HTML und ist mit einem
 * Handgriff sichtbar.
 */

export type Dichte = 'einfach' | 'experte';

/**
 * Die Vorgabe.
 *
 * Sie steht zusätzlich als `data-dichte="einfach"` am `<html>` in
 * `index.html`: Ohne Attribut griffe keine der beiden Regeln, und die einfache
 * Ansicht zeigte die Expertenzeilen mit — die Vorgabe wäre dann faktisch
 * „experte".
 */
export const DICHTE_VORGABE: Dichte = 'einfach';

/** Das Attribut am Wurzelelement, auf das `token/anwendung.css` hört. */
export const DICHTE_ATTRIBUT = 'data-dichte';

/**
 * Die Dichte zu einem Kontostand.
 *
 * Fehlt das Feld (Server ohne die Spalte, Antwort aus der Zeit davor), gilt
 * die einfache Fassung. Das ist die vorsichtige Richtung: Wer die Rohwerte
 * nicht kennt, bekommt sie nicht ungefragt vorgesetzt — und wer sie will, hat
 * den Schalter einmal umgelegt und findet ihn wieder.
 */
export function dichteVon(nutzer: { expertenansicht?: boolean } | null | undefined): Dichte {
  return nutzer?.expertenansicht ? 'experte' : DICHTE_VORGABE;
}

/** Zeigt diese Dichte die Rohwerte? */
export function istExperte(dichte: Dichte): boolean {
  return dichte === 'experte';
}

/**
 * Die Dichte, wie sie am Wurzelelement steht.
 *
 * Der Umweg über das Attribut ist Absicht: Es gibt genau **einen** Ort, an
 * dem die Dichte steht (`App.svelte` setzt ihn aus dem Kontostand), und die
 * Panels hängen nicht am Konto — der Planer bekommt keinen Nutzer gereicht
 * und soll auch keinen bekommen, nur um zu wissen, wie viel erklärt werden
 * muss. Wer den Wert braucht, liest ihn dort ab, wo `token/anwendung.css` ihn
 * ohnehin liest.
 *
 * Der Vergleich läuft gegen `'experte'` und nicht gegen `'einfach'`: Ein
 * unbekannter oder fehlender Wert soll die vorsichtige Fassung ergeben,
 * dieselbe Richtung wie bei `dichteVon`.
 */
export function dichteAusAttribut(wert: string | null | undefined): Dichte {
  return wert === 'experte' ? 'experte' : DICHTE_VORGABE;
}

/**
 * Strecke in der einfachen Fassung: gerundet und benannt.
 *
 * „rund 560 km" statt „558,4 km". Das ist keine Vereinfachung für Anfänger,
 * sondern die ehrlichere Zahl: Die 400 Meter hinter dem Komma sind das
 * Ergebnis einer Routenberechnung, die morgen anders ausfällt, weil eine
 * Baustelle dazwischenkommt. Wer sie trotzdem braucht, legt den Schalter um.
 *
 * Unter zehn Kilometern wird nicht gerundet — „rund 0 km" wäre falsch, und
 * ein Umweg von 1,2 km ist genau die Zahl, auf die es dort ankommt.
 */
export function distanzRund(meter: number | null | undefined): string {
  if (meter == null) return '–';
  if (meter < 10_000) {
    return `${(meter / 1000).toLocaleString('de-DE', { maximumFractionDigits: 1 })} km`;
  }
  return `rund ${Math.round(meter / 10_000) * 10} km`;
}

/**
 * Dauer in der einfachen Fassung: „gut 2 Stunden" statt „2 Std. 17 Min.".
 *
 * „etwa" bis 20 Minuten über der vollen Stunde, „gut" darüber — dieselbe
 * Unterscheidung, die man beim Sprechen trifft. Unter einer Stunde bleibt es
 * bei den Minuten, weil „etwa 0 Stunden" nichts sagt.
 */
export function dauerRund(ms: number | null | undefined): string {
  if (ms == null) return '–';
  const min = Math.round(ms / 60_000);
  if (min < 60) return `${min} Min.`;
  const std = Math.floor(min / 60);
  const rest = min % 60;
  const wort = std === 1 ? 'Stunde' : 'Stunden';
  return `${rest > 20 ? 'gut' : 'etwa'} ${std} ${wort}`;
}

/**
 * Der Schalter heißt „Expertenansicht", nicht „Einfache Ansicht".
 *
 * Das ist die Wahrheit und unterstellt nichts: Wer ihn aus lässt, ist kein
 * Anfänger, sondern jemand, der die Rohwerte nicht braucht. Die umgekehrte
 * Formulierung hätte genau das umgedreht.
 */
export const DICHTE_SCHALTER = 'Expertenansicht';

/** Was der Schalter bewirkt — der Satz darunter. */
export const DICHTE_WIRKUNG =
  'Zeigt rohe Werte statt gerundeter: Koordinaten, kWh, Tarife und Buchungsnummern. Reiter und Gliederung bleiben gleich.';
